import React, { useState } from 'react';
import { Box, Typography, Badge, Avatar, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Group as GroupIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { Conversation } from '../../types/chat';
import ChatAvatar from './ChatAvatar';

interface ConversationItemProps {
  conversation: Conversation;
  selected?: boolean;
  onClick: () => void;
  onToggleStar?: (conversationId: number) => void;
  onToggleArchive?: (conversationId: number) => void;
  onDelete?: (conversationId: number) => void;
  onRestore?: (conversationId: number) => void;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  selected = false,
  onClick,
  onToggleStar,
  onToggleArchive,
  onDelete,
  onRestore,
}) => {
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleToggleStar = () => {
    onToggleStar?.(conversation.id);
    handleCloseContextMenu();
  };

  const handleToggleArchive = () => {
    onToggleArchive?.(conversation.id);
    handleCloseContextMenu();
  };

  const handleDelete = () => {
    onDelete?.(conversation.id);
    handleCloseContextMenu();
  };

  const handleRestore = () => {
    onRestore?.(conversation.id);
    handleCloseContextMenu();
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isToday(date)) {
        return format(date, 'HH:mm');
      }
      if (isYesterday(date)) {
        return 'Hier';
      }
      return format(date, 'dd/MM/yy', { locale: fr });
    } catch {
      return '';
    }
  };

  const getPreviewText = () => {
    if (!conversation.last_message) return 'No messages yet';

    const msg = conversation.last_message;
    if (msg.attachments && msg.attachments.length > 0) {
      const attachmentCount = msg.attachments.length;
      const attachmentText = attachmentCount === 1 ? '1 attachment' : `${attachmentCount} attachments`;
      return msg.content ? `${msg.content} (${attachmentText})` : attachmentText;
    }
    return msg.content || '';
  };

  const otherParticipant = conversation.other_participant;
  const isGroup = conversation.is_group;

  // Get display name for conversation
  const getDisplayName = () => {
    if (isGroup) {
      return conversation.group_name || 'Groupe sans nom';
    }
    return otherParticipant?.full_name || 'Inconnu';
  };

  // Get initials for group avatar
  const getGroupInitials = () => {
    const name = conversation.group_name || 'G';
    return name.substring(0, 2).toUpperCase();
  };

  // For groups, check if any member is online
  const hasOnlineMember = isGroup && conversation.participants.some((p) => p.user.is_online);

  return (
    <>
    <Box
      onClick={onClick}
      onContextMenu={handleContextMenu}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        cursor: 'pointer',
        backgroundColor: selected ? 'action.selected' : 'transparent',
        borderLeft: selected ? `3px solid ${OUTLOOK_BLUE}` : '3px solid transparent',
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': {
          backgroundColor: selected ? 'action.selected' : 'action.hover',
        },
      }}
    >
      {/* Avatar */}
      {isGroup ? (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              backgroundColor: OUTLOOK_BLUE,
              fontSize: '1rem',
            }}
          >
            {conversation.group_avatar_url ? (
              <img src={conversation.group_avatar_url} alt={conversation.group_name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <GroupIcon />
            )}
          </Avatar>
          {/* Online indicator for group */}
          {hasOnlineMember && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 10,
                height: 10,
                backgroundColor: '#4caf50',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
          )}
        </Box>
      ) : otherParticipant ? (
        <ChatAvatar
          name={otherParticipant.full_name}
          isOnline={otherParticipant.is_online}
          lastSeenAt={otherParticipant.last_seen_at}
          size={44}
        />
      ) : null}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: conversation.unread_count > 0 ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getDisplayName()}
            </Typography>
            {isGroup && (
              <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                ({conversation.participants.length})
              </Typography>
            )}
          </Box>
          {conversation.last_message && (
            <Typography
              variant="caption"
              sx={{
                color: conversation.unread_count > 0 ? OUTLOOK_BLUE : 'text.secondary',
                fontWeight: conversation.unread_count > 0 ? 600 : 400,
                ml: 1,
                flexShrink: 0,
              }}
            >
              {formatDate(conversation.last_message.created_at)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: conversation.unread_count > 0 ? 500 : 400,
            }}
          >
            {getPreviewText()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            {/* Star indicator */}
            {conversation.is_starred && (
              <StarIcon sx={{ fontSize: 16, color: '#ffc107' }} />
            )}
            {/* Unread badge */}
            {conversation.unread_count > 0 && (
              <Badge
                badgeContent={conversation.unread_count}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: OUTLOOK_BLUE,
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Context Menu */}
    <Menu
      open={contextMenu !== null}
      onClose={handleCloseContextMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
      PaperProps={{
        sx: { borderRadius: 2, minWidth: 180 }
      }}
    >
      {onToggleStar && (
        <MenuItem onClick={handleToggleStar}>
          <ListItemIcon>
            {conversation.is_starred ? (
              <StarIcon fontSize="small" sx={{ color: '#ffc107' }} />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {conversation.is_starred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          </ListItemText>
        </MenuItem>
      )}
      {onToggleArchive && (
        <MenuItem onClick={handleToggleArchive}>
          <ListItemIcon>
            {conversation.is_archived ? (
              <UnarchiveIcon fontSize="small" />
            ) : (
              <ArchiveIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {conversation.is_archived ? 'Désarchiver' : 'Archiver'}
          </ListItemText>
        </MenuItem>
      )}
      {onDelete && (
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Supprimer</ListItemText>
        </MenuItem>
      )}
      {onRestore && (
        <MenuItem onClick={handleRestore} sx={{ color: 'success.main' }}>
          <ListItemIcon>
            <RestoreIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Restaurer</ListItemText>
        </MenuItem>
      )}
    </Menu>
    </>
  );
};

export default ConversationItem;
